import { supabaseClient } from '@/services/supabase';
import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';
import { maybeThrowError } from '@/utils/error-helpers';
import type {
    AuthUser,
    BackendUserViewRow,
    InviteUserRow,
    MergedUserViewRow,
    ParticipantViewRow,
    UserRole,
    UserRow,
    UserUpdate,
} from '@/types/auth';
import { useAuthStore } from '@/stores/auth';
import { isNullable } from '@/utils/nullability-helpers';
import { EdgeFunctionsManager } from '@/utils/edge-functions-manager';

// Dedupe key for merging invites + members: email (lowercased) when present, else
// phone reduced to digits so an E.164 invite (+15551234567) and the accepted
// member's GoTrue phone (15551234567) collapse to a single row.
function userIdentityKey(row: { email?: string | null; phone?: string | null }): string | null {
    return row.email?.toLowerCase() ?? row.phone?.replace(/\D/g, '') ?? null;
}

export async function updateUser(userID: string, { firstName, lastName }: UserUpdate): Promise<void> {
    const { error } = await supabaseClient.from('users').upsert(decamelizeKeys({ id: userID, firstName, lastName }));
    maybeThrowError(error, 'errors.frontend.userSaveFailed');
}

export async function updateUserOrganizationRole(
    userId: string,
    organizationId: string,
    role: UserRole,
): Promise<void> {
    await EdgeFunctionsManager.invokeEdgeFunction('update-user-organization-role', {
        userId,
        organizationId,
        role,
    });
}

export async function getParticipantsWithDepartmentRoles(organizationId: string): Promise<
    Array<
        MergedUserViewRow & {
            departmentRoles: Array<{
                departmentId: string;
                departmentRoleId: string;
                departmentName: string;
                roleName: string;
            }>;
        }
    >
> {
    const [membersDBResult, deptRolesResult, invitesResult] = await Promise.all([
        supabaseClient.from('participants').select('*').eq('organization_id', organizationId),

        supabaseClient
            .from('department_roles_users_relations')
            .select(
                `
                user_id,
                department_id,
                department_role_id,
                departments!department_roles_users_relations_department_id_fkey(
                    dept_name
                ),
                department_roles!department_roles_users_relations_department_role_id_fkey(
                    role_name
                )
            `,
            )
            .eq('organization_id', organizationId),

        supabaseClient
            .from('organization_invites')
            .select(
                `
                id,
                invited_email,
                invited_phone,
                roles,
                created_at,
                organization_id,
                user_id,
                cohort_id,
                organization_invites_department_roles(
                    department_id,
                    department_role_id,
                    departments!organization_invites_department_roles_department_id_fkey(
                        dept_name
                    ),
                    department_roles!organization_invites_department_roles_department_role_id_fkey(
                        role_name
                    )
                )
            `,
            )
            .eq('organization_id', organizationId)
            .is('accepted_at', null)
            .contains('roles', ['participant']),
    ]);

    maybeThrowError(membersDBResult.error, 'errors.frontend.usersFetchFailed');
    maybeThrowError(deptRolesResult.error, 'errors.frontend.usersFetchFailed');
    maybeThrowError(invitesResult.error, 'errors.frontend.usersFetchFailed');

    const deptRolesByUser = new Map<string, NonNullable<typeof deptRolesResult.data>>();
    for (const dr of deptRolesResult.data ?? []) {
        if (!dr.user_id) continue;
        const list = deptRolesByUser.get(dr.user_id) ?? [];
        list.push(dr);
        deptRolesByUser.set(dr.user_id, list);
    }

    const membersResult =
        membersDBResult.data?.map((member) => ({
            ...member,
            department_roles: member.id ? (deptRolesByUser.get(member.id) ?? []) : [],
        })) ?? [];

    const transformedMembers = (membersResult ?? []).map((user) => ({
        ...user,
        department_roles:
            user.department_roles?.map((role) => ({
                department_id: role.department_id,
                department_role_id: role.department_role_id,
                department_name: role.departments?.dept_name,
                role_name: role.department_roles?.role_name,
            })) || [],
    })) as unknown as ParticipantViewRow[];

    const members = camelcaseKeys(transformedMembers, { deep: true }) as Array<
        ParticipantViewRow & {
            departmentRoles: Array<{
                departmentId: string;
                departmentRoleId: string;
                departmentName: string;
                roleName: string;
            }>;
        }
    >;

    // Transform invites to match user row format
    const transformedInvites = camelcaseKeys(invitesResult.data ?? [], { deep: true }) as any[];
    const invites: Array<
        InviteUserRow & {
            departmentRoles: Array<{
                departmentId: string;
                departmentRoleId: string;
                departmentName: string;
                roleName: string;
            }>;
        }
    > = transformedInvites
        .filter((invite: any) => {
            const isParticipant = invite.roles && invite.roles.includes('participant');
            return isParticipant;
        })
        .map((invite: any) => {
            const createdAt = new Date(invite.createdAt);
            const hoursSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
            const confirmationLinkExpired = hoursSinceCreation > 24;

            // Transform department roles from invite
            const departmentRoles = (invite.organizationInvitesDepartmentRoles || []).map((deptRole: any) => ({
                departmentId: deptRole.departmentId,
                departmentRoleId: deptRole.departmentRoleId,
                departmentName: deptRole.departments?.deptName || '-',
                roleName: deptRole.departmentRoles?.roleName || '-',
            }));

            return {
                id: invite.id,
                inviteId: invite.id,
                email: invite.invitedEmail ?? null,
                phone: invite.invitedPhone ?? null,
                role: invite.roles[0] as UserRole,
                organizationId: invite.organizationId,
                confirmed: false,
                confirmationLinkExpired,
                firstName: null,
                lastName: null,
                isInvite: true,
                cohortId: invite.cohortId ?? null,
                departmentRoles,
            };
        });

    // Merge and dedupe by identity — email, or phone for phone-only users (members win over invites)
    const identityMap = new Map<
        string,
        MergedUserViewRow & {
            departmentRoles: Array<{
                departmentId: string;
                departmentRoleId: string;
                departmentName: string;
                roleName: string;
            }>;
        }
    >();

    // Add invites first
    invites.forEach((invite) => {
        const key = userIdentityKey(invite);
        if (isNullable(key)) return;
        identityMap.set(key, invite);
    });

    // Add members (will overwrite invites if identity matches)
    members.forEach((member) => {
        const key = userIdentityKey(member);
        if (isNullable(key)) return;
        identityMap.set(key, member);
    });

    return Array.from(identityMap.values());
}

export async function getBackendUsersWithDepartmentRoles(organizationId: string): Promise<
    Array<
        MergedUserViewRow & {
            departmentRoles: Array<{
                departmentId: string;
                departmentRoleId: string;
                departmentName: string;
                roleName: string;
            }>;
        }
    >
> {
    const [membersDBResult, deptRolesResult, invitesResult] = await Promise.all([
        supabaseClient.from('backend_users').select('*').eq('organization_id', organizationId),

        supabaseClient
            .from('department_roles_users_relations')
            .select(
                `
                user_id,
                department_id,
                department_role_id,
                departments!department_roles_users_relations_department_id_fkey(
                    dept_name
                ),
                department_roles!department_roles_users_relations_department_role_id_fkey(
                    role_name
                )
            `,
            )
            .eq('organization_id', organizationId),

        supabaseClient
            .from('organization_invites')
            .select(
                `
                id,
                invited_email,
                invited_phone,
                roles,
                created_at,
                organization_id,
                user_id,
                cohort_id,
                organization_invites_department_roles(
                    department_id,
                    department_role_id,
                    departments!organization_invites_department_roles_department_id_fkey(
                        dept_name
                    ),
                    department_roles!organization_invites_department_roles_department_role_id_fkey(
                        role_name
                    )
                )
            `,
            )
            .eq('organization_id', organizationId)
            .is('accepted_at', null)
            .or('roles.cs.{admin},roles.cs.{instructor},roles.cs.{department_admin},roles.cs.{manager}'),
    ]);

    maybeThrowError(membersDBResult.error, 'errors.frontend.usersFetchFailed');
    maybeThrowError(deptRolesResult.error, 'errors.frontend.usersFetchFailed');
    maybeThrowError(invitesResult.error, 'errors.frontend.usersFetchFailed');

    // Merge department_roles into members using a Map for O(n+m) instead of O(n*m) filter.
    const deptRolesByUser2 = new Map<string, NonNullable<typeof deptRolesResult.data>>();
    for (const dr of deptRolesResult.data ?? []) {
        if (!dr.user_id) continue;
        const list = deptRolesByUser2.get(dr.user_id) ?? [];
        list.push(dr);
        deptRolesByUser2.set(dr.user_id, list);
    }

    const membersResult =
        membersDBResult.data?.map((member) => ({
            ...member,
            department_roles: member.id ? (deptRolesByUser2.get(member.id) ?? []) : [],
        })) ?? [];

    const transformedMembers = (membersResult ?? []).map((user) => ({
        ...user,
        department_roles:
            user.department_roles?.map((role) => ({
                department_id: role.department_id,
                department_role_id: role.department_role_id,
                department_name: role.departments?.dept_name,
                role_name: role.department_roles?.role_name,
            })) || [],
    })) as unknown as BackendUserViewRow[];

    const members = camelcaseKeys(transformedMembers, { deep: true }) as Array<
        BackendUserViewRow & {
            departmentRoles: Array<{
                departmentId: string;
                departmentRoleId: string;
                departmentName: string;
                roleName: string;
            }>;
        }
    >;

    // Transform invites to match user row format
    const transformedInvites = camelcaseKeys(invitesResult.data ?? [], { deep: true }) as any[];
    const backendRoles = ['admin', 'instructor', 'department_admin', 'manager'];
    const invites: Array<
        InviteUserRow & {
            departmentRoles: Array<{
                departmentId: string;
                departmentRoleId: string;
                departmentName: string;
                roleName: string;
            }>;
        }
    > = transformedInvites
        .filter((invite: any) => {
            const hasBackendRole = invite.roles && invite.roles.some((role: string) => backendRoles.includes(role));
            return hasBackendRole;
        })
        .map((invite: any) => {
            const createdAt = new Date(invite.createdAt);
            const hoursSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
            const confirmationLinkExpired = hoursSinceCreation > 24;

            // Transform department roles from invite
            const departmentRoles = (invite.organizationInvitesDepartmentRoles || []).map((deptRole: any) => ({
                departmentId: deptRole.departmentId,
                departmentRoleId: deptRole.departmentRoleId,
                departmentName: deptRole.departments?.deptName || '-',
                roleName: deptRole.departmentRoles?.roleName || '-',
            }));

            return {
                id: invite.id,
                inviteId: invite.id,
                email: invite.invitedEmail ?? null,
                phone: invite.invitedPhone ?? null,
                role: invite.roles[0] as UserRole,
                organizationId: invite.organizationId,
                confirmed: false,
                confirmationLinkExpired,
                firstName: null,
                lastName: null,
                isInvite: true,
                cohortId: invite.cohortId ?? null,
                departmentRoles,
            };
        });

    // Merge and dedupe by identity — email, or phone for phone-only users (members win over invites)
    const identityMap = new Map<
        string,
        MergedUserViewRow & {
            departmentRoles: Array<{
                departmentId: string;
                departmentRoleId: string;
                departmentName: string;
                roleName: string;
            }>;
        }
    >();

    // Add invites first
    invites.forEach((invite) => {
        const key = userIdentityKey(invite);
        if (isNullable(key)) return;
        identityMap.set(key, invite);
    });

    // Add members (will overwrite invites if identity matches)
    members.forEach((member) => {
        const key = userIdentityKey(member);
        if (isNullable(key)) return;
        identityMap.set(key, member);
    });

    return Array.from(identityMap.values());
}

export async function getCurrentUser(): Promise<UserRow | undefined> {
    const { fetchUserIfNecessary } = useAuthStore();
    const user = await fetchUserIfNecessary();

    if (isNullable(user)) {
        return undefined;
    }

    const { data, error } = await supabaseClient.from('users').select(`*`).eq('id', user.id).maybeSingle();

    maybeThrowError(error, 'errors.frontend.currentUserFetchFailed');

    if (isNullable(data)) {
        return undefined;
    }
    return camelcaseKeys(data);
}

export async function updateCurrentUser(update: UserUpdate): Promise<void> {
    const { fetchUserIfNecessary } = useAuthStore();
    const user = (await fetchUserIfNecessary()) as AuthUser;
    const updateWithId = { ...update, id: user.id };

    const { error } = await supabaseClient.from('users').upsert(decamelizeKeys(updateWithId));
    maybeThrowError(error, 'errors.frontend.userSaveFailed');
}

/**
 * Persists the user's explicit UI language choice to their profile so it follows them across
 * devices and can localize server-generated email. No-op when signed out — the choice still lives
 * in local storage for the current browser. Only writes `preferred_language`, leaving other
 * profile columns untouched (upsert updates just the provided keys).
 */
export async function persistPreferredLanguage(preferredLanguage: string): Promise<void> {
    const { fetchUserIfNecessary } = useAuthStore();
    const user = await fetchUserIfNecessary();
    if (isNullable(user)) {
        return;
    }

    const { error } = await supabaseClient.from('users').upsert(decamelizeKeys({ id: user.id, preferredLanguage }));
    maybeThrowError(error, 'errors.frontend.userSaveFailed');
}

export async function updateUserDepartmentRoles(
    userId: string,
    organizationId: string,
    departmentRoleIds?: string[],
): Promise<void> {
    // Delete all existing role assignments for this user
    const { error: deleteError } = await supabaseClient
        .from('department_roles_users_relations')
        .delete()
        .eq('user_id', userId)
        .eq('organization_id', organizationId);

    maybeThrowError(deleteError, 'errors.frontend.userSaveFailed');

    // Insert new role assignments
    if (departmentRoleIds && departmentRoleIds.length > 0) {
        // First get department IDs for these roles
        const { data: roles } = await supabaseClient
            .from('department_roles')
            .select('id, department_id')
            .in('id', departmentRoleIds)
            .eq('organization_id', organizationId);

        if (!roles) return;

        const { error: insertError } = await supabaseClient.from('department_roles_users_relations').insert(
            roles.map((role) => ({
                user_id: userId,
                department_id: role.department_id,
                department_role_id: role.id,
                organization_id: organizationId,
            })),
        );

        maybeThrowError(insertError, 'errors.frontend.userSaveFailed');
    }

    // Fire-and-forget: recompute recommendations for the user after role assignment/change.
    // Errors here must not block the save.
    supabaseClient.functions
        .invoke('enqueue-course-recommendation-jobs', {
            body: { userId, organizationId },
        })
        .catch((err) => {
            console.warn('[updateUserDepartmentRoles] Failed to enqueue recommendation job:', err);
        });
}

export async function canManageDepartment(
    departmentId: string,
    userId: string | undefined,
    organizationId: string,
): Promise<boolean> {
    if (isNullable(userId)) {
        return false;
    }

    // Fetch all departments for the org in one query, then traverse the hierarchy in-memory.
    const { data: allDepts } = await supabaseClient
        .from('departments')
        .select('id, parent_dept_id')
        .eq('organization_id', organizationId);

    if (!allDepts) return false;

    const parentMap = new Map(allDepts.map((d) => [d.id, d.parent_dept_id]));

    if (!parentMap.has(departmentId)) return false;

    const deptIds: string[] = [departmentId];
    let currentId: string | null | undefined = parentMap.get(departmentId);
    while (currentId) {
        deptIds.push(currentId);
        currentId = parentMap.get(currentId);
    }

    const { data } = await supabaseClient
        .from('department_roles_users_relations')
        .select('department_id')
        .eq('user_id', userId)
        .eq('organization_id', organizationId)
        .in('department_id', deptIds);

    return data !== null && data.length > 0;
}

export async function verifyUserEmail(email: string): Promise<boolean> {
    const { data: participantData } = await supabaseClient.from('participants').select('email').eq('email', email);
    const { data: backendUserData } = await supabaseClient.from('backend_users').select('email').eq('email', email);

    return (
        (participantData?.some((participant) => participant.email === email) ||
            backendUserData?.some((user) => user.email === email)) ??
        false
    );
}

export async function addUserName(id: string, firstName: string, lastName: string): Promise<void> {
    // Use a database function that can bypass RLS during sign-up
    const { error } = await supabaseClient.rpc('create_user_profile', {
        user_id: id,
        first_name: firstName,
        last_name: lastName,
    });
    maybeThrowError(error, 'errors.frontend.userSaveFailed');
}
